import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  fetchScorecardConfigById,
  resetSelectedScorecardConfig,
} from '@/reducers/scorecards/scorecards.actions'
import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import { getTagsWithCategories } from '@/utils/helpers'
import {
  rankOnOptions,
  qaRankIdentifiers,
  timePeriodOptions,
  QA_CRITERION_RANK_IDENTIFIER,
} from '@/utils/constants'

import { getSchema } from './leaderboard.schema'
import { getLeaderboardInitialValues, getLeaderboardOptions } from '../helpers'

export const LeaderboardForm = ({
  isEdit,
  selectedLeaderboard,
  handleSubmit,
  onClose,
  ...props
}) => {
  const initialRankIdentifier = selectedLeaderboard?.version?.rank_identifier
  const [isQA, setIsQA] = useState(qaRankIdentifiers.includes(initialRankIdentifier))
  const [isQACriterion, setIsQACriterion] = useState(
    initialRankIdentifier === QA_CRITERION_RANK_IDENTIFIER
  )
  const { qa_enabled: qaEnabled, qa_copilot_enabled: copilotEnabled } = useSelector(
    (state) => state.currentOrganization
  )
  const dispatch = useDispatch()

  const {
    organizationTags: tags,
    organizationTagCategories: tagCategories,
    playbooks,
    scorecards,
    leaderboards,
  } = useSelector((state) => state)
  const { data: leaderboardsData } = leaderboards
  const { data: scorecardsData } = scorecards
  const playbookList = playbooks.playbooks || []
  const scorecardConfigOptions = useMemo(
    () =>
      scorecardsData?.scorecardConfigs?.map((scorecard) => ({
        value: scorecard.sid,
        label: scorecard.name,
        id: scorecard.id,
      })),
    [scorecardsData?.scorecardConfigs]
  )

  const playbookOptions = playbookList.map((playbook) => ({
    label: playbook.name,
    value: playbook.cid,
  }))
  const tagsOptionsWithCategories = getTagsWithCategories(tags, tagCategories)
  const initialValues = getLeaderboardInitialValues(selectedLeaderboard, tags, playbookList)
  const leaderboardOptions = getLeaderboardOptions(leaderboardsData.leaderboards, initialValues)

  const determineIfQASelected = ({ value }) => {
    setIsQACriterion(value === QA_CRITERION_RANK_IDENTIFIER)
    if (qaRankIdentifiers.includes(value)) {
      setIsQA(true)
    } else {
      setIsQA(false)
      dispatch(resetSelectedScorecardConfig())
    }
  }

  const handleScorecardSelect = ({ id }) => {
    if (!id) {
      dispatch(resetSelectedScorecardConfig())
      return
    }

    dispatch(fetchScorecardConfigById(id))
  }

  // A criterion only means something inside one measure. In an OR measure a
  // criterion can score 0 while the measure passes on a sibling, so ranking
  // agents on it would read as a failure that never happened -- those are
  // left out of the picker rather than silently mis-ranked.
  // Disabling a section only flips section.active (AddScorecardForm's
  // updateStatusInObject); its measures and criteria stay active, so the
  // section has to be filtered in its own right.
  const { sections = [] } = scorecardsData?.targetScorecardConfig ?? {}
  const criteriaOptions = sections
    .filter((section) => section.active !== false)
    .flatMap(({ name: sectionName, measures = [] }) =>
      measures
        .filter((measure) => measure.active !== false && measure.condition_type !== 'OR')
        .flatMap(({ criteria = [] }) =>
          criteria
            .filter((criterion) => criterion.active !== false && criterion.scid)
            .map((criterion) => ({
              value: criterion.scid,
              label: `${sectionName} / ${criterion.name}`,
            }))
        )
    )

  const orgAccessibleRankOptions =
    qaEnabled || copilotEnabled
      ? rankOnOptions
      : rankOnOptions.filter((option) => !qaRankIdentifiers.includes(option.value))

  const schema = getSchema({
    isQA,
    isQACriterion,
    criteriaOptions,
    determineIfQASelected,
    handleScorecardSelect,
    scorecardValues: scorecardsData.targetScorecardConfig,
    playbookOptions,
    scorecardConfigOptions,
    rankOnOptions: orgAccessibleRankOptions,
    timePeriodOptions,
    tagsOptionsWithCategories,
    leaderboardOptions,
  })

  // fetch scorecard data to populate the scorecard cheatsheet
  useEffect(() => {
    if (isQA && initialValues.scorecard_config_sid) {
      const matchingScorecardConfigOption = scorecardConfigOptions.find(
        (scorecardConfig) => scorecardConfig.value === initialValues.scorecard_config_sid
      )

      if (matchingScorecardConfigOption) {
        dispatch(fetchScorecardConfigById(matchingScorecardConfigOption.id))
      }
    }
  }, [scorecardConfigOptions])

  return (
    <AbstractForm
      data-testid="leaderboard-form"
      existingValues={isEdit ? initialValues : {}}
      schema={schema}
      buttonLabel={isEdit ? 'Update' : 'Create'}
      onSubmit={async (values) => {
        const saved = await handleSubmit(values, selectedLeaderboard)

        // Only an explicit failure keeps the form open; a handler that returns
        // nothing closes it as before.
        if (saved !== false && onClose) {
          onClose()
        }
      }}
      onClose={onClose}
      isModal
      {...props}
    />
  )
}
